import {
  ApiBlankFaceEnum,
  BlankSideEnum,
  BlankSingleSideEnum,
  type BlankTextData,
  type CanvasTransformation,
  type PrintArea,
} from "./blank";

import { ProductTypeEnum } from "./product";

export enum CustomizationElementTypeEnum {
  IMAGE = "IMAGE",
  TEXT = "TEXT",
}

export enum ContainerTypeEnum {
  CART = "cart",
  WISHLIST = "wishlist",
}

export interface ImagePpi {
  x: number;
  y: number;
}

export enum CustomizationImageKind {
  PRINT = "PRINT",
  LOW_QUALITY_PREVIEW = "LOW_QUALITY_PREVIEW",
}

export enum CustomizationImageQualityStatus {
  GOOD = "GOOD",
  WARNING = "WARNING",
  UNKNOWN = "UNKNOWN",
}

export interface ProductionPrintMetrics {
  widthCm: number;
  heightCm: number;
  widthPx: number;
  heightPx: number;
  targetDpi: 200;
  effectiveDpi: number | null;
  qualityStatus: CustomizationImageQualityStatus;
  offsetXMillimeters: number;
  offsetYMillimeters: number;
  printAreaWidthCm: number;
  printAreaHeightCm: number;
  renderVersion: "production-print-v1";
}

export interface CustomizationImageData extends Partial<ProductionPrintMetrics> {
  kind: CustomizationImageKind;
  side: BlankSingleSideEnum;
  imageUrl: string;
}

export interface ApiCustomizationImageData extends Omit<CustomizationImageData, "side"> {
  side: ApiBlankFaceEnum.FRONT | ApiBlankFaceEnum.BACK;
}

export interface ICustomizationDetails {
  id: string;
  version: number;
  galeryImageUrls: string[];
  previewImageUrls: string[];
  printSide: BlankSideEnum;
  images?: CustomizationImageData[];
  colors: string[];
  sizes: string[];
  mainColorHex: string;
  mainColorName: string;
  mainSide: BlankSingleSideEnum;
  details: {
    [k in BlankSingleSideEnum]?: {
      area?: PrintArea;
      elements: CustomizationElementWithSize[];
      imageSrc: string;
      printArea: PrintArea;
    };
  };
  type: ProductTypeEnum;
}

export interface ImageElementToLoad {
  degradedEl: HTMLImageElement | null;
  el: HTMLImageElement | null;
  id: string;
  hasError?: boolean;
}

export interface CustomizationImageElement extends CanvasTransformation {
  id: string;
  element: HTMLImageElement;
  degradedElement: HTMLImageElement; // Versão degradada para refletir qualidade final da imagem na impressão
  fileName?: string;
  type: CustomizationElementTypeEnum.IMAGE;
  width?: number;
  height?: number;
  offset?: { x: number; y: number };
  sourcePpi?: ImagePpi;
}

export interface CustomizationTextElement extends BlankTextData {
  id: string;
  type: CustomizationElementTypeEnum.TEXT;
}

export type CustomizationElement = CustomizationImageElement | CustomizationTextElement;
export type CustomizationElementWithSize = (CustomizationImageElement | CustomizationTextElement) & {
  size: { w: number; h: number };
};

export interface ApiCustomizationDetails extends Omit<ICustomizationDetails, "printSide" | "mainSide" | "images"> {
  printSide: ApiBlankFaceEnum;
  mainSide: ApiBlankFaceEnum;
  images?: ApiCustomizationImageData[];
}

export interface GaleryImageFromApi {
  id: string;
  keywords: string[];
  image: string;
}

export interface GaleryImage {
  id: string;
  fileName?: string;
  imageSrc: string;
  sourcePpi?: ImagePpi;
}

/**
 * Elemento de imagem formatado para serialização/API
 */
export type FormattedImageElement = CanvasTransformation & {
  id: string;
  size: { h: number; w: number };
  src: string;
  type: CustomizationElementTypeEnum.IMAGE;
  fileName?: string;
};

/**
 * Elemento de texto formatado para serialização/API
 */
export type FormattedTextElement = BlankTextData & {
  id: string;
  size: { h: number; w: number };
  type: CustomizationElementTypeEnum.TEXT;
};

/**
 * Elementos formatados (imagem ou texto) prontos para serialização/API
 */
export type FormattedCustomizationElement = FormattedImageElement | FormattedTextElement;

/**
 * Dados finais de customização de um lado (frente ou costas) incluindo blob da imagem renderizada
 */
export interface CustomizationSideFinalData {
  /** Área efetivamente usada na customização */
  usedArea: PrintArea;
  /** Área de impressão disponível */
  printArea: PrintArea;
  /** Blob da imagem renderizada da área customizada */
  usedAreaBlob: Blob;
  /** Tamanho do canvas usado na renderização */
  canvasSize?: { w: number; h: number };
  /** Elementos da customização formatados para serialização */
  elements: FormattedCustomizationElement[];
}

/**
 * Dados finais da customização completa (ambos os lados possíveis)
 */
export type CustomizationFinalData = {
  [k in BlankSingleSideEnum]?: CustomizationSideFinalData;
};
